import { watch } from 'vue';
import { useAutopilotScope, useAutopilotStore } from '@llmor/autopilot-vue';

/**
 * Publishes an arbitrary value under `store.context[key]` for the assistant to read, and clears the
 * key when the owning component unmounts. Returns a `set` function for imperative updates (e.g. from
 * an event handler); pass a reactive `source` getter to publish automatically on every change.
 *
 * Always hand it a fresh object — the lib diffs context by reference, so mutating in place would not
 * sync. A `source` getter that builds a new object each call satisfies this for free, and its
 * dependencies are tracked precisely (no `deep` traversal of large payloads).
 *
 * Unmount only clears the key while this instance still owns it (`useAutopilotScope`), so a keyed
 * remount publishing the same key keeps the new instance's value.
 */
export function useAutopilotContext<T = unknown>(key: string, source?: () => T): (value: T) => void {
  const store = useAutopilotStore();
  useAutopilotScope().claimContext(key);

  const set = (value: T) => {
    if (value === undefined || value === null) {
      delete store.context[key];
      return;
    }
    store.context[key] = value;
  };

  if (source) {
    watch(source, (value) => set(value), { immediate: true });
  }

  return set;
}
